import { DISCIPLINES, REFERRAL_SOURCES, SALES_REPS } from "./constants";
import { computeValue } from "./compute";
import { fnv1a, makeRng } from "./seed";
import { baseId, grainOf } from "./pillars";
import { buildRoster } from "./roster";
import type { DateRange, Metric, Service } from "./types";

/* ============================================================================
   DIMENSIONAL DRILL — categorical breakdown (bars + table + records).
   ========================================================================== */
export interface Dimension { label: string; members: string[]; rec: string; }
/** Branch and payor members are empty here on purpose — they are live, per-user
 *  data from `dashboard/config`. Resolve them with `dimensionMembers()`, which
 *  takes the lists off the store; the rest are still seeded pools. */
export const DIMENSIONS: Record<string, Dimension> = {
  discipline: { label: "Discipline", members: DISCIPLINES, rec: "clinician" },
  branch: { label: "Branch", members: [], rec: "branch" },
  payor: { label: "Payor", members: [], rec: "claim" },
  source: { label: "Referral Source", members: REFERRAL_SOURCES, rec: "referral" },
  rep: { label: "Sales Rep", members: SALES_REPS, rec: "referral" },
  aging: { label: "AR Aging", members: ["0–30", "31–60", "61–90", "90+"], rec: "claim" },
  casemix: { label: "Case-Mix", members: ["Comm. Early", "Comm. Late", "Inst. Early", "Inst. Late"], rec: "episode" },
};

/** The member list for a dimension, substituting the live branch/payor lists. */
export function dimensionMembers(dimKey: string, branches: string[], payors: string[]): string[] {
  if (dimKey === "branch") return branches;
  if (dimKey === "payor") return payors;
  return DIMENSIONS[dimKey]?.members ?? [];
}

const AR_IDS = ["ar_bal", "ar_aging", "ar_60", "ar_120", "ar_age", "unbilled", "unbilled_pct", "dso", "ar_eps", "bad_debt", "ncr", "netrev", "gross_rev", "rev_adj"];
const PDGM_IDS = ["lupa", "ep_mix", "revpp", "vpp", "gmpp", "recert", "rec2p", "casemix_ce", "casemix_cl", "casemix_ie", "casemix_il", "starts2p", "admits2p"];

export function dimsForMetric(m: Metric): string[] {
  const g = grainOf(m.id), base = baseId(m.id);
  let dims: string[];
  if (g.startsWith("Clinician")) dims = ["discipline", "branch"];
  else if (g.startsWith("Referral")) dims = ["source", "rep", "branch"];
  else if (g.startsWith("Payor")) dims = ["payor", "branch"];
  else if (g.startsWith("Order")) dims = ["branch"];
  else dims = ["branch", "payor"];
  if (AR_IDS.includes(base)) dims = ["aging", "payor", "branch"];
  else if (PDGM_IDS.includes(base)) dims = ["casemix", "payor", "branch"];
  return dims;
}

export const isAdditive = (m: Metric): boolean => (m.kind === "count" || m.kind === "currency") && m.agg !== "avg";

function catWeights(metric: Metric, dimKey: string, members: string[]): number[] {
  const rng = makeRng(fnv1a(metric.id + "|" + dimKey));
  const raw = members.map(() => 0.45 + rng());
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((r) => r / sum);
}

function catValue(metric: Metric, dimKey: string, member: string, idx: number, members: string[], range: DateRange): number {
  const total = computeValue(metric, range);
  if (isAdditive(metric)) return total * catWeights(metric, dimKey, members)[idx];
  const rng = makeRng(fnv1a(metric.id + "|" + dimKey + "|" + member));
  let v = total * (1 + (rng() - 0.5) * 0.42);
  if (metric.kind === "pct") v = Math.max(0, Math.min(100, v));
  return Math.max(0, v);
}

export interface CategoryRecord { name: string; sub: string; value: number; }
export function categoryRecords(metric: Metric, dimKey: string, member: string, range: DateRange, service: Service, branches: string[]): CategoryRecord[] {
  const base = computeValue(metric, range);
  const rng = makeRng(fnv1a(metric.id + "|" + dimKey + "|" + member + "|rec"));
  if (dimKey === "discipline") {
    const ros = buildRoster(service, branches).filter((c) => c.disc === member).slice(0, 10);
    if (ros.length) return ros.map((c) => ({ name: c.name, sub: c.job, value: base * (0.7 + rng() * 0.6) }));
  }
  if (dimKey === "branch") {
    const ros = buildRoster(service, branches).filter((c) => c.branch === member).slice(0, 10);
    if (ros.length) return ros.map((c) => ({ name: c.name, sub: c.job, value: base * (0.7 + rng() * 0.6) }));
  }
  const pfx = dimKey === "payor" || dimKey === "aging" ? "Claim" : (dimKey === "source" || dimKey === "rep") ? "Referral" : "Episode";
  const n = 6 + Math.floor(rng() * 5);
  return Array.from({ length: n }, () => ({ name: `${pfx} #${1000 + Math.floor(rng() * 8999)}`, sub: member, value: base * (0.6 + rng() * 0.8) }));
}

export interface BreakdownRow { mem: string; value: number; }
export function breakdownRows(metric: Metric, dimKey: string, range: DateRange, members: string[]): BreakdownRow[] {
  return members.map((mem, i) => ({ mem, value: catValue(metric, dimKey, mem, i, members, range) }))
    .sort((a, b) => (metric.lowerBetter ? a.value - b.value : b.value - a.value));
}
